import { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import { useI18n } from "../i18n";

const ExitIntent = () => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false);
  const fallbackRef = useRef(null);

  const onLeave = useCallback(
    (e) => {
      if (e.clientY <= 0 && !shown) {
        setOpen(true);
        setShown(true);
        sessionStorage.setItem("exit_intent_shown", "1");
      }
    },
    [shown, setOpen, setShown]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem("exit_intent_shown") === "1") return;

    // Fallback for mobile: trigger after long inactivity
    fallbackRef.current = setTimeout(() => {
      if (!shown && window.scrollY > 800) {
        setOpen(true);
        setShown(true);
        sessionStorage.setItem("exit_intent_shown", "1");
      }
    }, 45000);

    document.addEventListener("mouseout", onLeave);
    return () => {
      document.removeEventListener("mouseout", onLeave);
      clearTimeout(fallbackRef.current);
    };
  }, [shown, onLeave, setOpen, setShown]);

  const close = () => setOpen(false);
  const goto = () => {
    close();
    setTimeout(() => {
      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm"
          onClick={close}
          data-testid="exit-intent-overlay"
        >
          <motion.div
            initial={{ y: 30, scale: 0.95, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ type: "spring", damping: 22 }}
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-md w-full glass-strong rounded-3xl p-8 md:p-10 text-center"
            data-testid="exit-intent-modal"
          >
            <button
              onClick={close}
              className="absolute top-4 right-4 text-white/50 hover:text-white"
              data-testid="exit-intent-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="inline-flex px-3 py-1 rounded-full bg-[#A60056]/15 border border-[#A60056]/30 text-[#A60056] text-xs uppercase tracking-wider mb-5 font-semibold">
              {t.exit.cta}
            </div>
            <h3 className="text-2xl md:text-3xl text-white font-medium mb-3 text-balance">
              {t.exit.title}
            </h3>
            <p className="text-white/60 mb-7 leading-relaxed text-[15px]">{t.exit.subtitle}</p>
            <button onClick={goto} className="btn-primary w-full" data-testid="exit-intent-cta">
              {t.exit.cta}
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={close}
              className="block mt-4 text-xs text-white/40 hover:text-white/70 mx-auto"
              data-testid="exit-intent-dismiss"
            >
              {t.exit.dismiss}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ExitIntent;
